import React, { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import {
  Building2, Calendar, Users, Check, Crown, Zap, Shield, ArrowUpRight, Loader2, Save,
  PenTool, Trash2, KeyRound,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";

const PLAN_COLORS = {
  starter: "bg-slate-100 text-slate-700 border-slate-200",
  professional: "bg-cyan-50 text-cyan-700 border-cyan-200",
  enterprise: "bg-amber-50 text-amber-700 border-amber-200",
};

const PLAN_ICONS = { starter: Shield, professional: Zap, enterprise: Crown };

export function SubscriptionTab() {
  const { user } = useAuth();
  const isSuperuser = user?.role === "superuser";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [org, setOrg] = useState(null);
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [orgRes, plansRes] = await Promise.all([
        apiClient.get("/organization"),
        apiClient.get("/organization/plans"),
      ]);
      setOrg(orgRes.data);
      setPlans(plansRes.data.plans);
      setAddons(plansRes.data.addons);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const [editOrg, setEditOrg] = useState(null);

  useEffect(() => {
    if (org) {
      setEditOrg({
        org_name: org.org_name || "",
        admin_contact_email: org.admin_contact_email || "",
        admin_contact_name: org.admin_contact_name || "",
        plan: org.plan?.plan || "starter",
        plan_start_date: org.plan?.plan_start_date || "",
        plan_expiry_date: org.plan?.plan_expiry_date || "",
        billing_cycle: org.plan?.billing_cycle || "annual",
        addons: org.plan?.addons || [],
        max_users_override: null,
        notes: org.notes || "",
      });
    }
  }, [org]);

  const handleSave = async () => {
    if (!editOrg) return;
    setSaving(true);
    try {
      const payload = { ...editOrg };
      if (!payload.max_users_override) delete payload.max_users_override;
      await apiClient.put("/organization", payload);
      toast.success("Organization settings updated");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const toggleAddon = (addonId) => {
    if (!editOrg) return;
    const current = editOrg.addons || [];
    const updated = current.includes(addonId) ? current.filter(a => a !== addonId) : [...current, addonId];
    setEditOrg({ ...editOrg, addons: updated });
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 size={24} className="animate-spin text-slate-400" /></div>;
  if (!org || !editOrg) return <p className="text-sm text-slate-400 text-center py-12">Unable to load organization info</p>;

  const planDef = plans.find(p => p.id === org.plan?.plan) || plans[0];
  const PlanIcon = PLAN_ICONS[org.plan?.plan] || Shield;

  return (
    <div className="space-y-6" data-testid="subscription-tab">
      {/* Current Plan Overview */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${PLAN_COLORS[org.plan?.plan]}`}>
                <PlanIcon size={24} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">{org.plan?.label} Plan</h3>
                  {org.plan?.is_expired && <Badge className="bg-red-100 text-red-700 rounded-sm text-[10px]">EXPIRED</Badge>}
                </div>
                <p className="text-sm text-slate-500">{planDef?.description}</p>
              </div>
            </div>
            <Badge className={`rounded-sm ${PLAN_COLORS[org.plan?.plan]}`}>{org.plan?.billing_cycle}</Badge>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-5">
            <div className="border rounded-sm p-3 text-center">
              <Users size={18} className="mx-auto text-slate-400 mb-1" />
              <p className="text-xl font-bold" data-testid="user-count">{org.user_count}</p>
              <p className="text-xs text-slate-500">of {org.plan?.max_users} seats</p>
              <div className="mt-2 bg-slate-100 rounded-full h-1.5">
                <div className={`h-1.5 rounded-full ${org.user_count > org.plan?.max_users ? "bg-red-500" : "bg-emerald-500"}`}
                  style={{ width: `${Math.min(100, (org.user_count / org.plan?.max_users) * 100)}%` }} />
              </div>
            </div>
            <div className="border rounded-sm p-3 text-center">
              <Calendar size={18} className="mx-auto text-slate-400 mb-1" />
              <p className="text-sm font-medium" data-testid="plan-start">{org.plan?.plan_start_date || "—"}</p>
              <p className="text-xs text-slate-500">Start date</p>
            </div>
            <div className="border rounded-sm p-3 text-center">
              <Calendar size={18} className="mx-auto text-slate-400 mb-1" />
              <p className={`text-sm font-medium ${org.plan?.is_expired ? "text-red-600" : ""}`} data-testid="plan-expiry">{org.plan?.plan_expiry_date || "—"}</p>
              <p className="text-xs text-slate-500">Expiry date</p>
            </div>
          </div>

          {/* Included Features — toggleable for superuser */}
          <div className="mt-5">
            <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
              Feature Controls ({org.plan?.all_enabled_features?.length} active)
            </p>
            {isSuperuser ? (
              <FeatureToggleGrid
                planFeatures={org.plan?.features || []}
                addons={org.plan?.addons || []}
                disabledFeatures={org.plan?.disabled_features || []}
                onToggle={async (feature, enabled) => {
                  try {
                    await apiClient.put("/organization/feature-toggle", { feature, enabled });
                    toast.success(`${feature.replace(/_/g, " ")} ${enabled ? "enabled" : "disabled"}`);
                    fetchData();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed to toggle feature");
                  }
                }}
              />
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {(org.plan?.all_enabled_features || []).map(f => (
                  <Badge key={f} variant="secondary" className="text-[10px] rounded-sm gap-1">
                    <Check size={10} className="text-emerald-500" />{f.replace(/_/g, " ")}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Active Addons */}
          {org.plan?.addons?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Active Add-ons</p>
              <div className="flex gap-2">
                {org.plan.addons.map(a => {
                  const addon = addons.find(x => x.id === a);
                  return (
                    <Badge key={a} className="bg-purple-50 text-purple-700 border-purple-200 rounded-sm text-xs gap-1">
                      <Zap size={10} />{addon?.label || a}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Superuser: Plan Configuration */}
      {isSuperuser && (
        <Card>
          <CardContent className="pt-6 space-y-5">
            <div>
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Crown size={16} className="text-amber-500" /> Plan Configuration
                <Badge className="bg-red-50 text-red-700 border-red-200 rounded-sm text-[10px]">SUPERUSER ONLY</Badge>
              </h3>
              <p className="text-xs text-slate-500 mt-1">Configure the client's subscription plan, features, and add-ons.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Organization Name</Label>
                <Input value={editOrg.org_name} onChange={e => setEditOrg({ ...editOrg, org_name: e.target.value })} className="rounded-sm" data-testid="org-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Admin Contact Email</Label>
                <Input value={editOrg.admin_contact_email} onChange={e => setEditOrg({ ...editOrg, admin_contact_email: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Admin Contact Name</Label>
                <Input value={editOrg.admin_contact_name} onChange={e => setEditOrg({ ...editOrg, admin_contact_name: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Subscription Plan</Label>
                <Select value={editOrg.plan} onValueChange={v => setEditOrg({ ...editOrg, plan: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {plans.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.label} — {p.max_users} users, {p.features.length} features
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Plan Start Date</Label>
                <Input type="date" value={editOrg.plan_start_date} onChange={e => setEditOrg({ ...editOrg, plan_start_date: e.target.value })} className="rounded-sm" data-testid="plan-start-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Plan Expiry Date</Label>
                <Input type="date" value={editOrg.plan_expiry_date} onChange={e => setEditOrg({ ...editOrg, plan_expiry_date: e.target.value })} className="rounded-sm" data-testid="plan-expiry-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Billing Cycle</Label>
                <Select value={editOrg.billing_cycle} onValueChange={v => setEditOrg({ ...editOrg, billing_cycle: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Add-ons */}
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Premium Add-ons</p>
              <div className="space-y-2">
                {addons.map(addon => (
                  <div key={addon.id} className="flex items-center justify-between p-3 border rounded-sm" data-testid={`addon-${addon.id}`}>
                    <div>
                      <p className="text-sm font-medium">{addon.label}</p>
                      <p className="text-xs text-slate-500">{addon.description}</p>
                    </div>
                    <Switch
                      checked={editOrg.addons.includes(addon.id)}
                      onCheckedChange={() => toggleAddon(addon.id)}
                      data-testid={`addon-toggle-${addon.id}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Textarea value={editOrg.notes} onChange={e => setEditOrg({ ...editOrg, notes: e.target.value })} placeholder="Internal notes about this deployment..." rows={2} className="rounded-sm" />
            </div>

            <Button onClick={handleSave} disabled={saving} className="gap-2 rounded-sm" data-testid="save-plan-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save Configuration
            </Button>
          </CardContent>
        </Card>
      )}

      {/* E-Signature Provider Configuration — Admin + Superuser, gated by plan */}
      {(isSuperuser || user?.role === "admin") && (
        (user?.premium_features?.includes("e_signature") || isSuperuser) ? (
          <ESignConfigCard />
        ) : (
          <Card data-testid="esign-upgrade-card">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <PenTool size={18} className="text-slate-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-700">E-Signature Provider</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Electronic signature integration requires the <strong>Professional</strong> or <strong>Enterprise</strong> plan.
                    Your current plan does not include this feature.
                  </p>
                  <p className="text-xs text-slate-400 mt-1">Print & Sign (wet-ink) is available on all plans.</p>
                  <p className="text-xs text-slate-400 mt-2">Contact your platform administrator to upgrade.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )
      )}

      {/* Non-superuser: Contact for upgrade */}
      {!isSuperuser && (
        <Card>
          <CardContent className="pt-6 text-center">
            <Crown size={24} className="mx-auto text-amber-400 mb-2" />
            <h3 className="text-sm font-semibold">Need more features or seats?</h3>
            <p className="text-xs text-slate-500 mt-1 mb-3">Contact your system administrator to upgrade your plan or add premium features.</p>
            <Button variant="outline" className="rounded-sm gap-2" onClick={() => toast.info("Contact your administrator to request plan changes")}>
              <ArrowUpRight size={14} /> Request Upgrade
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function ESignConfigCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ provider: "docusign", api_key: "", account_id: "", base_url: "" });

  const fetchConfig = useCallback(async () => {
    try {
      const res = await apiClient.get("/organization/esign-config");
      setConfig(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    if (!form.api_key.trim()) { toast.error("API key is required"); return; }
    setSaving(true);
    try {
      await apiClient.put("/organization/esign-config", form);
      toast.success("E-signature provider configured");
      setEditing(false);
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleRemove = async () => {
    if (!window.confirm("Remove e-signature configuration? The tenant will revert to Print & Sign only.")) return;
    try {
      await apiClient.delete("/organization/esign-config");
      toast.success("E-signature configuration removed");
      setEditing(false);
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove");
    }
  };

  if (loading) return null;

  const isConfigured = config?.is_configured;

  return (
    <Card data-testid="esign-config-card">
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <PenTool size={16} className="text-cyan-600" /> E-Signature Provider
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Configure a third-party e-signature service for this tenant. Without it, Print & Sign (wet-ink) is the default.
            </p>
          </div>
          {isConfigured && !editing && (
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 rounded-sm text-xs gap-1" data-testid="esign-active-badge">
              <Check size={10} /> Active
            </Badge>
          )}
          {!isConfigured && !editing && (
            <Badge className="bg-slate-100 text-slate-500 rounded-sm text-xs" data-testid="esign-inactive-badge">
              Not configured
            </Badge>
          )}
        </div>

        {/* Current configuration display */}
        {isConfigured && !editing && (
          <div className="border rounded-sm p-4 bg-slate-50/50 space-y-3" data-testid="esign-current-config">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-cyan-100 flex items-center justify-center">
                <PenTool size={18} className="text-cyan-600" />
              </div>
              <div>
                <p className="font-medium text-sm">{config.supported_providers?.[config.provider]?.label || config.provider}</p>
                <p className="text-xs text-slate-400">Configured {config.configured_at ? new Date(config.configured_at).toLocaleDateString() : ""} by {config.configured_by}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-slate-500 uppercase font-mono">API Key</p>
                <p className="font-mono text-slate-700" data-testid="esign-masked-key">{config.api_key_masked}</p>
              </div>
              {config.account_id && (
                <div>
                  <p className="text-slate-500 uppercase font-mono">Account ID</p>
                  <p className="font-mono text-slate-700">{config.account_id}</p>
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" className="rounded-sm gap-1.5" onClick={() => { setForm({ provider: config.provider, api_key: "", account_id: config.account_id || "", base_url: config.base_url || "" }); setEditing(true); }} data-testid="esign-edit-btn">
                <KeyRound size={12} /> Update Credentials
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm gap-1.5 text-red-600 border-red-200 hover:bg-red-50" onClick={handleRemove} data-testid="esign-remove-btn">
                <Trash2 size={12} /> Remove
              </Button>
            </div>
          </div>
        )}

        {/* Setup / Edit form */}
        {(!isConfigured || editing) && (
          <div className="border rounded-sm p-4 space-y-3" data-testid="esign-form">
            <div className="space-y-1">
              <Label className="text-xs">Provider</Label>
              <Select value={form.provider} onValueChange={v => setForm({ ...form, provider: v })}>
                <SelectTrigger className="rounded-sm" data-testid="esign-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {config?.supported_providers && Object.entries(config.supported_providers).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">API Key *</Label>
              <Input
                type="password"
                value={form.api_key}
                onChange={e => setForm({ ...form, api_key: e.target.value })}
                placeholder={editing ? "Enter new API key (leave blank to keep existing)" : "Paste your API key"}
                className="rounded-sm font-mono text-sm"
                data-testid="esign-api-key-input"
              />
            </div>
            {form.provider === "docusign" && (
              <>
                <div className="space-y-1">
                  <Label className="text-xs">Account ID</Label>
                  <Input
                    value={form.account_id}
                    onChange={e => setForm({ ...form, account_id: e.target.value })}
                    placeholder="DocuSign Account ID (GUID)"
                    className="rounded-sm font-mono text-sm"
                    data-testid="esign-account-id-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Base URL (optional)</Label>
                  <Input
                    value={form.base_url}
                    onChange={e => setForm({ ...form, base_url: e.target.value })}
                    placeholder="https://demo.docusign.net/restapi"
                    className="rounded-sm font-mono text-sm"
                    data-testid="esign-base-url-input"
                  />
                </div>
              </>
            )}
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving} className="rounded-sm gap-2 bg-cyan-600 hover:bg-cyan-700" data-testid="esign-save-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {editing ? "Update" : "Save Configuration"}
              </Button>
              {editing && (
                <Button variant="outline" onClick={() => setEditing(false)} className="rounded-sm">Cancel</Button>
              )}
            </div>
          </div>
        )}

        {/* Info banner */}
        {!isConfigured && !editing && (
          <div className="bg-cyan-50/50 border border-cyan-200 rounded-sm p-3 text-xs text-cyan-800">
            <p className="font-medium">Print & Sign is the default signing method</p>
            <p className="text-cyan-600 mt-0.5">Configure an e-signature provider below to enable electronic signing alongside wet-ink signatures.</p>
            <Button variant="outline" size="sm" className="rounded-sm gap-1.5 mt-2 border-cyan-300 text-cyan-700 hover:bg-cyan-100" onClick={() => setEditing(true)} data-testid="esign-setup-btn">
              <PenTool size={12} /> Set Up E-Signature Provider
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function FeatureToggleGrid({ planFeatures, addons, disabledFeatures, onToggle }) {
  const disabled = React.useMemo(() => new Set(disabledFeatures || []), [disabledFeatures]);
  const sortedFeatures = React.useMemo(
    () => [...new Set([...planFeatures, ...addons])].sort(),
    [planFeatures, addons]
  );

  const LABELS = {
    contract_drafting: "Contract Drafting",
    templates: "Templates",
    clause_library: "Clause Library",
    basic_workflows: "Basic Workflows",
    governed_workflows: "Governed Workflows",
    delegation: "Delegation of Authority",
    basic_compliance: "Basic Compliance",
    full_compliance: "Full Compliance",
    ai_extraction: "AI Extraction",
    guided_wizard: "Creation Wizard",
    contract_health: "Contract Health (AI)",
    bulk_upload: "Bulk Upload",
    e_signature: "E-Signature",
    basic_reports: "Basic Reports",
    full_reports: "Full Reports",
    basic_rbac: "Basic RBAC",
    full_rbac: "Full RBAC",
    audit_log: "Audit Log",
    api_access: "API Access",
    sso: "SSO",
    decision_intelligence: "Decision Intelligence",
    notices: "Notices Module",
    supplier_review: "Supplier Review",
    ai_template_generation: "AI Template Generation",
    ai_clause_generation: "AI Clause Generation",
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="feature-toggle-grid">
      {sortedFeatures.map(f => {
        const isEnabled = !disabled.has(f);
        return (
          <div key={f} className={`flex items-center justify-between p-2 border rounded-sm text-xs ${isEnabled ? "bg-white border-slate-200" : "bg-slate-50 border-slate-100 opacity-60"}`}
               data-testid={`feature-toggle-${f}`}>
            <span className="truncate mr-2 font-medium">{LABELS[f] || f.replace(/_/g, " ")}</span>
            <Switch
              checked={isEnabled}
              onCheckedChange={(checked) => onToggle(f, checked)}
              className="scale-75"
              data-testid={`feature-switch-${f}`}
            />
          </div>
        );
      })}
    </div>
  );
}
